import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Col, message, Popconfirm, Row, Spin, Table, Tooltip } from 'antd';
import {
	DeleteTwoTone,
	EyeOutlined,
	InfoCircleOutlined,
	LoadingOutlined,
	PlayCircleTwoTone,
} from '@ant-design/icons';
import CreateMarketingCampaignModal from './MarketingCampaignModal/CreateMarketingCampaignModal';
import Search from 'antd/lib/input/Search';
import Column from 'antd/lib/table/Column';
import ProgressCircle from './ProgressCircle';
import { FormattedMessage } from 'react-intl';
import { apiV2 } from 'services/api';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { Auths } from 'interfaces/bitrix24';
import { SyncButton } from 'components/SyncButton';

import './MarketingCampaignTable.css';
import { LanguageContext } from 'contexts/LanguageContext';
import CrossSellMarketingCampaign from 'components/CrossSellBanners/CrossSellMarketingCampaign';
import CampaignItemsModal from './MarketingCampaignModal/CampaignItems/CampaignItemsModal';
import { AudienceItem } from 'interfaces/marketingCampaign';
import CampaignRescheduleModal from './MarketingCampaignModal/CampaignItems/CampaignRescheduleModal';
import { Moment } from 'moment';

export interface Items {
	id: string;
	title: string;
	scheduledTime: string;
	remaningTime: string;
	isOnQueue: boolean;
	sent: number;
	delivered: number;
	read: number;
	total: number;
	shouldReschedule: boolean;
	statusDescription: string;
	audiences: AudienceItem[];
}

interface MarketingCampaignTableProps {
	items: Items[];
	isLoading: boolean;
	onStartCampaign: (id: string) => void;
	onSyncCampaigns: () => void;
}

export default function MarketignCampaignTable(props: MarketingCampaignTableProps) {
	const [loadingReschedule, setLoadingReschedule] = React.useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [modalCampaignItems, setModalCampaignItems] = useState(false);
	const [modalRescheduleCampaign, setModalRescheduleCampaign] = useState(false);
	const [campaignItems, setCampaignItems] = useState<Items | undefined>();
	const [filteredCampaigns, setFilteredCampaigns] = useState<Items[]>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [auths, setAuths] = useState<Auths>({ access_token: '', refresh_token: '' });
	const [domain, setDomain] = useState<string>();
	const [loadingCampaignId, setLoadingCampaignId] = useState<string[]>([]);
	const [startingCampaignIds, setStartingCampaignIds] = useState<string[]>([]);
	const [startDisabled, setStartDisabled] = useState(false);
	const bitrix24 = useContext(Bitrix24Context);
	const { dictionary } = useContext(LanguageContext);

	const headers = {
		'Access-Token': auths.access_token,
		'Refresh-Token': auths.refresh_token,
	};

	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		setAuths(bitrix24.auth);
	}, [bitrix24.auth, bitrix24.infos.domain]);

	useEffect(() => {
		const filtered = searchCampaigns(searchQuery);
		const formatted = formatItems(filtered);
		setFilteredCampaigns(formatted);
	}, [searchQuery, props.items]);

	function formatItems(items: Items[]) {
		return items.map((item) => {
			const sent = Math.round((item.sent * 100) / item.total);
			const delivered = Math.round((item.delivered * 100) / item.total);
			const read = Math.round((item.read * 100) / item.total);
			return { ...item, sent, delivered, read };
		});
	}

	function searchCampaigns(query: string) {
		return props.items.filter((campaign: any) => {
			return Object.keys(campaign).some((key) => {
				if (typeof campaign[key] === 'string') {
					return campaign[key].toLowerCase().includes(query.toLowerCase());
				}
				return false;
			});
		});
	}

	function handleSearchChange(event: any) {
		setSearchQuery(event.target.value);
	}

	function handleModalVisible() {
		setModalVisible(!modalVisible);
	}

	function openModalCampaignItems(item: Items) {
		setCampaignItems(item);
		setModalCampaignItems(true);
	}

	function closeModalCampaignItems() {
		setCampaignItems(undefined);
		setModalCampaignItems(false);
	}

	function openModalRescheduleCampaign(item: Items) {
		setCampaignItems(item);
		setModalRescheduleCampaign(true);
	}

	function closeModalRescheduleCampaign() {
		setCampaignItems(undefined);
		setModalRescheduleCampaign(false);
	}

	function handleCampaignFinish() {
		setModalVisible(false);
		props.onSyncCampaigns();
	}

	function isCampaignSent(item: Items) {
		if (item.sent > 0 || item.total > 0) return true;
	}

	function isManualCampaign(item: Items) {
		if (item.sent > 0 || item.total === 0 || item.isOnQueue) return false;
		return item.scheduledTime === dictionary.send_manually;
	}

	function startCampaign(id: string) {
		setStartingCampaignIds((prev) => [...prev, id]);
		apiV2.post(`marketing-campaign/${id}/start?domain=${domain}`, {}, { headers }).then(() => {
			setStartingCampaignIds((prev) => prev.filter((item) => item !== id));
			props.onStartCampaign(id);
		});
	}

	function rescheduleCampaign(id: string, scheduleDate: Moment) {
		setLoadingReschedule(true);
		apiV2
			.post(
				`marketing-campaign/${id}/reschedule?domain=${domain}`,
				{ scheduleDate: scheduleDate.toISOString() },
				{ headers },
			)
			.then((res) => {
				message.success(dictionary.reschedule_message_success);
			})
			.catch((error) => {
				console.log('error', error);
				message.error(dictionary.reschedule_message_error);
			})
			.finally(() => {
				setLoadingReschedule(false);
				closeModalRescheduleCampaign();
				props.onSyncCampaigns();
			});
	}

	function isCampaignStarting(id: string) {
		return startingCampaignIds.includes(id);
	}

	function isCampaignLoading(id?: string) {
		if (!id) return loadingCampaignId.length >= 1;
		return loadingCampaignId.includes(id);
	}

	function getOriginalItem(id: string) {
		return props.items.find((item) => item.id === id);
	}

	async function deleteCampaign(id: string, index: number) {
		setLoadingCampaignId((prev) => [...prev, id]);
		await apiV2.delete(`marketing-campaign/${domain}/${id}`, { headers }).then(() => {
			const newCampaigns = filteredCampaigns;
			newCampaigns.splice(index, 1);
			setFilteredCampaigns(newCampaigns);
		});
		setLoadingCampaignId((prev) => prev.filter((item) => item !== id));
	}

	return (
		<>
			<CreateMarketingCampaignModal
				visible={modalVisible}
				onCancel={handleModalVisible}
				onOk={handleCampaignFinish}
			/>
			<CampaignItemsModal
				open={modalCampaignItems}
				onClose={closeModalCampaignItems}
				campaign={campaignItems}
			/>
			<CampaignRescheduleModal
				open={modalRescheduleCampaign}
				onClose={closeModalRescheduleCampaign}
				campaign={campaignItems}
				onRescheduleCampaign={rescheduleCampaign}
				loadingReschedule={loadingReschedule}
			/>
			<Card
				bodyStyle={{ padding: 0 }}
				title={
					<>
						<Row style={{ display: 'flex', alignItems: 'center' }}>
							<Col span={24} style={{ display: 'flex' }}>
								<Search
									style={{ width: '100%', marginRight: 8 }}
									size="large"
									onChange={handleSearchChange}
									placeholder={dictionary.search}
								/>
								<Button size="large" onClick={handleModalVisible} type="primary">
									<FormattedMessage id="new_campaign" />
								</Button>
							</Col>
						</Row>
						<Row>
							<CrossSellMarketingCampaign />
						</Row>
					</>
				}
			>
				<Table
					style={{ fontSize: 14 }}
					loading={{
						spinning: props.isLoading,
						indicator: (
							<Spin
								style={{ margin: -23 }}
								indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
							/>
						),
					}}
					dataSource={filteredCampaigns}
				>
					<Column title={dictionary.campaign_name} dataIndex="title" />
					<Column
						title={dictionary.scheduled_for}
						dataIndex="scheduledTime"
						render={(_: number, record: Items) => {
							if (record.sent === 0 && record.shouldReschedule) {
								return (
									<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<span
											style={{
												color: '#1890ff',
												cursor: 'pointer',
												textDecoration: 'underline',
											}}
											onClick={() => openModalRescheduleCampaign(record)}
										>
											{dictionary.reschedule}
										</span>

										<Tooltip
											title={
												record.statusDescription == 'campaign_not_send'
													? dictionary.campaign_not_send
													: dictionary.campaign_not_send_openline
											}
										>
											<InfoCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
										</Tooltip>
									</div>
								);
							}

							return record.scheduledTime;
						}}
					/>
					<Column title={dictionary.remaining_time} dataIndex="remaningTime" />
					<Column
						align="center"
						title={dictionary.sent}
						dataIndex="sent"
						key="sent"
						className="py-0 px-1"
						render={(value: number, record: Items) => (
							<Tooltip
								title={`${getOriginalItem(record.id)?.sent} ${dictionary.of} ${record.total}`}
							>
								<div>
									<ProgressCircle percentage={value} />
								</div>
							</Tooltip>
						)}
					/>
					<Column
						align="center"
						title={dictionary.delivered}
						dataIndex="delivered"
						key="delivered"
						className="py-0 px-1"
						render={(value: number, record: Items) => (
							<Tooltip
								title={`${getOriginalItem(record.id)?.delivered} ${dictionary.of} ${record.total}`}
							>
								<div>
									<ProgressCircle percentage={value} />
								</div>
							</Tooltip>
						)}
					/>
					<Column
						align="center"
						title={dictionary.read}
						dataIndex="read"
						key="read"
						className="py-0 px-1"
						render={(value: number, record: Items) => (
							<Tooltip
								title={`${getOriginalItem(record.id)?.read} ${dictionary.of} ${record.total}`}
							>
								<div>
									<ProgressCircle percentage={value} />
								</div>
							</Tooltip>
						)}
					/>
					<Column
						title={<SyncButton onClick={props.onSyncCampaigns} loading={props.isLoading} />}
						align="right"
						colSpan={4}
						key="actions"
						dataIndex="id"
						render={(value: string, record: Items, index) => {
							return (
								<Col>
									{isManualCampaign(record) && (
										<Tooltip title={dictionary.start_sending_now}>
											<Button
												style={{ fontSize: 24 }}
												type="link"
												size="default"
												disabled={startDisabled}
												loading={isCampaignStarting(value)}
												onClick={() => startCampaign(value)}
											>
												<PlayCircleTwoTone />
											</Button>
										</Tooltip>
									)}
									{isCampaignSent(record) && (
										<Tooltip
											overlayClassName="tooltip-centered"
											title={dictionary.view_campaign_items}
										>
											<Button
												type="link"
												size="default"
												style={{ fontSize: 24 }}
												onClick={() => openModalCampaignItems(record)}
											>
												<EyeOutlined />
											</Button>
										</Tooltip>
									)}

									<Popconfirm
										style={{ maxWidth: 400 }}
										title={dictionary.delete_campaign_confirmation}
										onConfirm={() => deleteCampaign(value, index)}
										okText={dictionary.yes}
										cancelText={dictionary.not}
									>
										<Button
											style={{ fontSize: 24 }}
											type="link"
											size="default"
											disabled={isCampaignLoading()}
											loading={isCampaignLoading(value)}
										>
											<DeleteTwoTone />
										</Button>
									</Popconfirm>
								</Col>
							);
						}}
					/>
				</Table>
			</Card>
		</>
	);
}
